import { createContext, useContext } from 'react';
import type { EditorView } from '@codemirror/view';
import { createStore, useStore } from '../core/store';
import type { Rect, Zone } from '../core/geometry';
import type { SessionState, WindowState } from '../core/session';
import type { Controller } from './controller';
import { ui, type UiState } from './ui';
import type { AuthState } from '../cloud/auth';
import type { SyncState } from '../cloud/engine';
import { summarize, type CloudSummary } from '../cloud/summary';

export const AppContext = createContext<Controller | null>(null);

export function useApp(): Controller {
  const app = useContext(AppContext);
  if (!app) throw new Error('MyTXT controller missing');
  return app;
}

export function useSession<S>(selector: (s: SessionState) => S): S {
  return useStore(useApp().session, selector);
}

export function useUi<S>(selector: (s: UiState) => S): S {
  return useStore(ui, selector);
}

export function useCloudAuth(): AuthState {
  const app = useApp();
  return useStore(app.cloud.auth.state, (s) => s);
}

export function useCloudSync(): SyncState | null {
  return useStore(useApp().cloud.sync, (s) => s);
}

/** The roll-up of account and synchronisation, re-evaluated when either (or the autosave switch) changes. */
export function useCloudSummary(): CloudSummary {
  const app = useApp();
  const auth = useCloudAuth();
  const sync = useCloudSync();
  const autosave = useUi((s) => s.prefs.cloudAutosave);
  return summarize(auth, sync, { autosave, configured: app.cloud.configured });
}

/** Which window a component belongs to. */
export const WindowContext = createContext<string | null>(null);

export function useWindowId(): string {
  const id = useContext(WindowContext);
  if (!id) throw new Error('Window context missing');
  return id;
}

const NO_WINDOW: WindowState = { id: '', docIds: [], activeId: null, rect: { x: 0, y: 0, w: 0, h: 0 }, zone: null, restore: null, z: 0 };

/** The component's own window (stable reference until that window changes). */
export function useWindow<S>(selector: (w: WindowState, s: SessionState) => S): S {
  const id = useWindowId();
  return useSession((s) => selector(s.windows.find((w) => w.id === id) ?? NO_WINDOW, s));
}

export function useIsFocused(): boolean {
  const id = useWindowId();
  return useSession((s) => s.focusedId === id);
}

/** The canvas-level hint shown while a dragged window would snap somewhere. */
export const snapPreview = createStore<{ rect: Rect; zone: Zone } | null>(null);

/** Where the pointer let go of the latest pointer selection (and the selection head it left). */
export const selectionRelease = createStore<{ x: number; y: number; head: number; view: EditorView } | null>(null);

/** Where windows would go for the layout under the pointer in the layout surface. */
export const layoutPreview = createStore<Rect[] | null>(null);
